import type {
  DoorDirection,
  RoomLayoutDoor,
  RoomLayoutShape,
  RoomLayoutTile,
  RoomLayoutWallEdge,
  RoomType,
} from "./types";
import { ROOM_TYPE_CONFIG } from "./types";

export interface LayoutBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export interface LayoutBoundaryEdge {
  x: number;
  z: number;
  direction: DoorDirection;
}

const DIRECTION_VECTORS: Record<DoorDirection, [number, number]> = {
  north: [0, -1],
  south: [0, 1],
  east: [1, 0],
  west: [-1, 0],
};

const DIRECTION_PRIORITY: DoorDirection[] = ["west", "north", "east", "south"];
const CARDINAL_DIRS: Array<[number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

export const MAX_LAYOUT_SIZE = 31;
export const MIN_LAYOUT_SIZE = 3;
export const MAX_TILE_HEIGHT = 12;
export const TILE_WORLD_HEIGHT_UNIT = 0.35;
export const TILE_HEIGHT_INCREMENT = 0.5;
export const ROOM_LAYOUT_ACCESS: readonly string[] = [
  "free",
  "premium",
  "unlockable",
] as const;
export const ROOM_LAYOUT_STATES: readonly string[] = [
  "draft",
  "published",
  "archived",
] as const;

export const DEFAULT_LAYOUT_IDS: Record<RoomType, string> = {
  home: "layout-home-classic",
  "town-hall": "layout-town-hall-classic",
  shop: "layout-shop-classic",
  gallery: "layout-gallery-classic",
  museum: "layout-museum-classic",
};

export const DEFAULT_LAYOUT_NAMES: Record<RoomType, string> = {
  home: "Classic Home",
  "town-hall": "Classic Town Hall",
  shop: "Classic Shop",
  gallery: "Classic Gallery",
  museum: "Classic Museum",
};

function parseDoorDirection(value: unknown): DoorDirection | null {
  if (value === "north" || value === "south" || value === "east" || value === "west") {
    return value;
  }
  return null;
}

function normalizeTileCell(value: unknown): RoomLayoutTile {
  if (typeof value === "boolean") {
    return {
      walkable: value,
      height: 0,
      surface: value ? "floor" : undefined,
    };
  }

  if (value && typeof value === "object") {
    const row = value as {
      walkable?: unknown;
      height?: unknown;
      surface?: unknown;
    };
    const walkable = Boolean(row.walkable);
    const height = clampTileHeight(
      Number.isFinite(Number(row.height)) ? Number(row.height) : 0
    );
    const surface =
      row.surface === "stairs" || row.surface === "floor"
        ? row.surface
        : walkable
        ? "floor"
        : undefined;
    return {
      walkable,
      height,
      surface,
    };
  }

  return {
    walkable: false,
    height: 0,
  };
}

export function clampTileHeight(value: number): number {
  const clamped = Math.max(0, Math.min(MAX_TILE_HEIGHT, value));
  return Math.round(clamped / TILE_HEIGHT_INCREMENT) * TILE_HEIGHT_INCREMENT;
}

function isValidTileHeightIncrement(value: number): boolean {
  if (!Number.isFinite(value)) {
    return false;
  }
  const normalized = clampTileHeight(value);
  return Math.abs(normalized - value) < 0.0001;
}

export function layoutCellKey(x: number, z: number): string {
  return `${x},${z}`;
}

export function layoutOrigin(layout: Pick<RoomLayoutShape, "width" | "height">): {
  originX: number;
  originZ: number;
} {
  return {
    originX: Math.floor(layout.width / 2),
    originZ: Math.floor(layout.height / 2),
  };
}

export function toWorldCell(
  layout: Pick<RoomLayoutShape, "width" | "height">,
  row: number,
  col: number
): [number, number] {
  const { originX, originZ } = layoutOrigin(layout);
  return [col - originX, row - originZ];
}

export function toGridCell(
  layout: Pick<RoomLayoutShape, "width" | "height">,
  x: number,
  z: number
): { row: number; col: number } | null {
  const { originX, originZ } = layoutOrigin(layout);
  const col = Math.round(x) + originX;
  const row = Math.round(z) + originZ;
  if (row < 0 || row >= layout.height || col < 0 || col >= layout.width) {
    return null;
  }
  return { row, col };
}

export function normalizeTiles(
  tiles: unknown,
  width: number,
  height: number
): RoomLayoutTile[][] {
  const normalized: RoomLayoutTile[][] = [];
  const rows = Array.isArray(tiles) ? tiles : [];
  for (let row = 0; row < height; row++) {
    const sourceRow = Array.isArray(rows[row]) ? (rows[row] as unknown[]) : [];
    const normalizedRow: RoomLayoutTile[] = [];
    for (let col = 0; col < width; col++) {
      normalizedRow.push(normalizeTileCell(sourceRow[col]));
    }
    normalized.push(normalizedRow);
  }
  return normalized;
}

export function parseLayoutTilesSafely(
  raw: string | null | undefined,
  width: number,
  height: number
): RoomLayoutTile[][] {
  if (!raw) {
    return normalizeTiles([], width, height);
  }
  try {
    return normalizeTiles(JSON.parse(raw), width, height);
  } catch {
    return normalizeTiles([], width, height);
  }
}

export function normalizeLayoutWalls(
  walls: unknown,
  layout: Pick<RoomLayoutShape, "width" | "height" | "tiles">
): RoomLayoutWallEdge[] {
  if (!Array.isArray(walls)) {
    return [];
  }

  const unique = new Set<string>();
  const normalized: RoomLayoutWallEdge[] = [];
  for (const value of walls) {
    if (!value || typeof value !== "object") continue;
    const record = value as { x?: unknown; z?: unknown; direction?: unknown };
    const x = Number(record.x);
    const z = Number(record.z);
    const direction = parseDoorDirection(record.direction);
    if (!Number.isFinite(x) || !Number.isFinite(z) || !direction) continue;
    const roundedX = Math.round(x);
    const roundedZ = Math.round(z);
    if (!isBoundaryEdge(layout as RoomLayoutShape, roundedX, roundedZ, direction)) {
      continue;
    }
    const key = `${roundedX},${roundedZ},${direction}`;
    if (unique.has(key)) continue;
    unique.add(key);
    normalized.push({
      x: roundedX,
      z: roundedZ,
      direction,
    });
  }
  return normalized;
}

export function parseLayoutWallsSafely(
  raw: string | null | undefined,
  layout: Pick<RoomLayoutShape, "width" | "height" | "tiles">
): RoomLayoutWallEdge[] | undefined {
  if (raw == null) {
    return undefined;
  }
  try {
    return normalizeLayoutWalls(JSON.parse(raw), layout);
  } catch {
    return undefined;
  }
}

export function createFilledTiles(width: number, height: number): RoomLayoutTile[][] {
  return Array.from({ length: height }, () =>
    Array.from({ length: width }, () => ({
      walkable: true,
      height: 0,
      surface: "floor" as const,
    }))
  );
}

export function getTileAt(
  layout: RoomLayoutShape,
  x: number,
  z: number
): RoomLayoutTile | null {
  const grid = toGridCell(layout, x, z);
  if (!grid) return null;
  return layout.tiles[grid.row]?.[grid.col] ?? null;
}

export function getTileHeight(
  layout: RoomLayoutShape,
  x: number,
  z: number
): number {
  return getTileAt(layout, x, z)?.height ?? 0;
}

export function getTileSurface(
  layout: RoomLayoutShape,
  x: number,
  z: number
): "floor" | "stairs" | undefined {
  return getTileAt(layout, x, z)?.surface;
}

export function getWalkableCells(
  layout: RoomLayoutShape
): Array<[number, number]> {
  const cells: Array<[number, number]> = [];
  for (let row = 0; row < layout.height; row++) {
    const tileRow = layout.tiles[row] ?? [];
    for (let col = 0; col < layout.width; col++) {
      const tile = tileRow[col];
      if (!tile?.walkable) continue;
      cells.push(toWorldCell(layout, row, col));
    }
  }
  return cells;
}

export function getWalkableSet(layout: RoomLayoutShape): Set<string> {
  const set = new Set<string>();
  for (const [x, z] of getWalkableCells(layout)) {
    set.add(layoutCellKey(x, z));
  }
  return set;
}

export function isWalkableCell(
  layout: RoomLayoutShape,
  x: number,
  z: number
): boolean {
  return Boolean(getTileAt(layout, x, z)?.walkable);
}

export function neighboringCell(
  x: number,
  z: number,
  direction: DoorDirection
): [number, number] {
  const [dx, dz] = DIRECTION_VECTORS[direction];
  return [x + dx, z + dz];
}

export function isBoundaryEdge(
  layout: RoomLayoutShape,
  x: number,
  z: number,
  direction: DoorDirection
): boolean {
  if (!isWalkableCell(layout, x, z)) {
    return false;
  }
  const [nx, nz] = neighboringCell(x, z, direction);
  return !isWalkableCell(layout, nx, nz);
}

export function listBoundaryEdges(layout: RoomLayoutShape): LayoutBoundaryEdge[] {
  const edges: LayoutBoundaryEdge[] = [];
  for (const [x, z] of getWalkableCells(layout)) {
    for (const direction of DIRECTION_PRIORITY) {
      if (isBoundaryEdge(layout, x, z, direction)) {
        edges.push({ x, z, direction });
      }
    }
  }
  return edges;
}

export function listExteriorBoundaryEdges(
  layout: RoomLayoutShape
): LayoutBoundaryEdge[] {
  const exteriorAir = gatherExteriorAirCells(layout);
  return listBoundaryEdges(layout).filter((edge) => {
    const [nx, nz] = neighboringCell(edge.x, edge.z, edge.direction);
    return exteriorAir.has(layoutCellKey(nx, nz));
  });
}

export function resolveLayoutWalls(layout: RoomLayoutShape): RoomLayoutWallEdge[] {
  if (Array.isArray(layout.walls)) {
    return normalizeLayoutWalls(layout.walls, layout);
  }
  return listExteriorBoundaryEdges(layout);
}

export function computeLayoutBounds(layout: RoomLayoutShape): LayoutBounds {
  const cells = getWalkableCells(layout);
  if (cells.length === 0) {
    const fallbackX = Math.max(1, Math.floor(layout.width / 2));
    const fallbackZ = Math.max(1, Math.floor(layout.height / 2));
    return {
      minX: -fallbackX,
      maxX: fallbackX,
      minZ: -fallbackZ,
      maxZ: fallbackZ,
    };
  }

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const [x, z] of cells) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  }
  return { minX, maxX, minZ, maxZ };
}

export function computeLayoutViewRadius(layout: RoomLayoutShape): number {
  const bounds = computeLayoutBounds(layout);
  const extent = Math.max(
    Math.abs(bounds.minX),
    Math.abs(bounds.maxX),
    Math.abs(bounds.minZ),
    Math.abs(bounds.maxZ)
  );
  return Math.max(4, extent + 2);
}

function gatherExteriorAirCells(layout: RoomLayoutShape): Set<string> {
  const bounds = computeLayoutBounds(layout);
  const minX = bounds.minX - 1;
  const maxX = bounds.maxX + 1;
  const minZ = bounds.minZ - 1;
  const maxZ = bounds.maxZ + 1;
  const visited = new Set<string>();
  const queue: Array<[number, number]> = [];

  const tryPush = (x: number, z: number) => {
    if (x < minX || x > maxX || z < minZ || z > maxZ) return;
    if (isWalkableCell(layout, x, z)) return;
    const key = layoutCellKey(x, z);
    if (visited.has(key)) return;
    visited.add(key);
    queue.push([x, z]);
  };

  for (let x = minX; x <= maxX; x++) {
    tryPush(x, minZ);
    tryPush(x, maxZ);
  }
  for (let z = minZ; z <= maxZ; z++) {
    tryPush(minX, z);
    tryPush(maxX, z);
  }

  while (queue.length > 0) {
    const [x, z] = queue.shift()!;
    for (const [dx, dz] of CARDINAL_DIRS) {
      tryPush(x + dx, z + dz);
    }
  }

  return visited;
}

function isDoorFacingExterior(
  layout: RoomLayoutShape,
  door: RoomLayoutDoor,
  exteriorAir: Set<string>
): boolean {
  if (!isBoundaryEdge(layout, door.x, door.z, door.direction)) {
    return false;
  }
  const [nx, nz] = neighboringCell(door.x, door.z, door.direction);
  return !isWalkableCell(layout, nx, nz) && exteriorAir.has(layoutCellKey(nx, nz));
}

export function chooseValidDoor(
  layout: RoomLayoutShape,
  candidate: RoomLayoutDoor
): RoomLayoutDoor {
  const exteriorAir = gatherExteriorAirCells(layout);
  const roundedCandidate: RoomLayoutDoor = {
    x: Math.round(candidate.x),
    z: Math.round(candidate.z),
    direction: candidate.direction,
  };

  if (isDoorFacingExterior(layout, roundedCandidate, exteriorAir)) {
    return roundedCandidate;
  }

  if (isWalkableCell(layout, roundedCandidate.x, roundedCandidate.z)) {
    for (const direction of DIRECTION_PRIORITY) {
      const next = {
        x: roundedCandidate.x,
        z: roundedCandidate.z,
        direction,
      } satisfies RoomLayoutDoor;
      if (isDoorFacingExterior(layout, next, exteriorAir)) {
        return next;
      }
    }
  }

  const edges = listBoundaryEdges(layout);
  for (const edge of edges) {
    const next: RoomLayoutDoor = {
      x: edge.x,
      z: edge.z,
      direction: edge.direction,
    };
    if (isDoorFacingExterior(layout, next, exteriorAir)) {
      return next;
    }
  }

  if (edges.length > 0) {
    const fallback = edges[0];
    return {
      x: fallback.x,
      z: fallback.z,
      direction: fallback.direction,
    };
  }

  return {
    x: 0,
    z: 0,
    direction: "west",
  };
}

export function getDoorExitCell(
  layout: Pick<RoomLayoutShape, "door">
): [number, number] {
  return neighboringCell(layout.door.x, layout.door.z, layout.door.direction);
}

export function getDoorSpawnPoint(
  layout: Pick<RoomLayoutShape, "door">,
  depth = 0
): [number, number] {
  const [dx, dz] = DIRECTION_VECTORS[layout.door.direction];
  // Door direction points toward exterior, so spawn offsets inward.
  // Depth=0 places the spawn on the doorway tile itself.
  return [layout.door.x - dx * depth, layout.door.z - dz * depth];
}

export function getDoorEntryFacing(
  layout: Pick<RoomLayoutShape, "door">
): number {
  switch (layout.door.direction) {
    case "west":
      return Math.PI / 2;
    case "east":
      return -Math.PI / 2;
    case "north":
      return 0;
    case "south":
      return Math.PI;
    default:
      return Math.PI / 2;
  }
}

export function createSquareLayoutFromRadius(gridRadius: number): RoomLayoutShape {
  const side = Math.max(MIN_LAYOUT_SIZE, gridRadius * 2 - 1);
  const half = Math.floor(side / 2);
  return {
    width: side,
    height: side,
    tiles: createFilledTiles(side, side),
    door: {
      x: -half,
      z: 0,
      direction: "west",
    },
  };
}

function createDefaultHomeLayout(): RoomLayoutShape {
  const width = 7;
  const height = 5;
  const rightWallX = Math.floor(width / 2);
  const topWallZ = -Math.floor(height / 2);
  return {
    width,
    height,
    tiles: createFilledTiles(width, height),
    door: {
      // 3rd tile from the right on the top wall.
      x: rightWallX - 2,
      z: topWallZ,
      direction: "north",
    },
  };
}

export function createDefaultLayoutForRoomType(
  roomType: RoomType
): RoomLayoutShape {
  if (roomType === "home") {
    return createDefaultHomeLayout();
  }
  const config = ROOM_TYPE_CONFIG[roomType] ?? ROOM_TYPE_CONFIG.home;
  return createSquareLayoutFromRadius(config.gridRadius);
}

export function isLegacySquareLayout(layout: RoomLayoutShape): boolean {
  if (layout.width !== layout.height || layout.width % 2 === 0) {
    return false;
  }
  for (let row = 0; row < layout.height; row++) {
    for (let col = 0; col < layout.width; col++) {
      const tile = layout.tiles[row]?.[col];
      if (!tile?.walkable || tile.height !== 0) {
        return false;
      }
    }
  }
  const half = Math.floor(layout.width / 2);
  return (
    layout.door.direction === "west" &&
    layout.door.x === -half &&
    layout.door.z >= -half &&
    layout.door.z <= half
  );
}

function walkableConnectivityCount(layout: RoomLayoutShape): number {
  const walkable = getWalkableCells(layout);
  if (walkable.length === 0) return 0;

  const seed = isWalkableCell(layout, layout.door.x, layout.door.z)
    ? ([layout.door.x, layout.door.z] as [number, number])
    : walkable[0];

  const visited = new Set<string>([layoutCellKey(seed[0], seed[1])]);
  const queue: Array<[number, number]> = [seed];

  while (queue.length > 0) {
    const [x, z] = queue.shift()!;
    for (const [dx, dz] of CARDINAL_DIRS) {
      const nx = x + dx;
      const nz = z + dz;
      if (!isWalkableCell(layout, nx, nz)) continue;
      const key = layoutCellKey(nx, nz);
      if (visited.has(key)) continue;
      visited.add(key);
      queue.push([nx, nz]);
    }
  }

  return visited.size;
}

export function validateLayoutShape(layout: RoomLayoutShape): string | null {
  if (
    !Number.isInteger(layout.width) ||
    !Number.isInteger(layout.height) ||
    layout.width < MIN_LAYOUT_SIZE ||
    layout.height < MIN_LAYOUT_SIZE ||
    layout.width > MAX_LAYOUT_SIZE ||
    layout.height > MAX_LAYOUT_SIZE
  ) {
    return `Layout width and height must be integers between ${MIN_LAYOUT_SIZE} and ${MAX_LAYOUT_SIZE}.`;
  }

  if (!Array.isArray(layout.tiles) || layout.tiles.length !== layout.height) {
    return "Layout tiles must match the declared height.";
  }
  for (const row of layout.tiles) {
    if (!Array.isArray(row) || row.length !== layout.width) {
      return "Each layout row must match the declared width.";
    }
    for (const cell of row) {
      if (
        !Number.isFinite(cell.height) ||
        cell.height < 0 ||
        cell.height > MAX_TILE_HEIGHT ||
        !isValidTileHeightIncrement(cell.height)
      ) {
        return `Tile heights must be between 0 and ${MAX_TILE_HEIGHT} in ${TILE_HEIGHT_INCREMENT} increments.`;
      }
    }
  }

  const walkable = getWalkableCells(layout);
  if (walkable.length === 0) {
    return "Layout must contain at least one walkable tile.";
  }

  const connectedCount = walkableConnectivityCount(layout);
  if (connectedCount !== walkable.length) {
    return "All walkable tiles must be connected in one traversable region.";
  }

  if (layout.walls !== undefined) {
    const normalizedWalls = normalizeLayoutWalls(layout.walls, layout);
    if (normalizedWalls.length !== layout.walls.length) {
      return "Walls must reference valid unique boundary edges.";
    }
  }

  const door = chooseValidDoor(layout, layout.door);
  if (
    door.x !== layout.door.x ||
    door.z !== layout.door.z ||
    door.direction !== layout.door.direction
  ) {
    return "Door must be on the exterior boundary of the walkable region and face outward.";
  }

  return null;
}
